import { useCallback } from "react";
import { create } from "zustand";

/**
 * A place in a page's own phone top lane for the M7 entry.
 *
 * On a phone M7 takes the empty top-right corner. A page that draws its own top
 * lane (the Trends feed: menu, title, then search and filters in one capsule)
 * has no empty corner, and the floating mark would sit on its buttons. Such a
 * page marks a slot in its lane instead, and the rail renders the entry there,
 * as one of the lane's buttons (library Bar / Top lane · Phone, where M7 is an
 * action in the lane's capsule).
 */
type PhoneLaneSlotStore = {
  slot: HTMLElement | null;
  claim: (slot: HTMLElement) => void;
  /** Only clears the slot it was handed, so a lane swapping one slot for
   *  another never loses the newer one. */
  release: (slot: HTMLElement) => void;
};

export const usePhoneLaneSlot = create<PhoneLaneSlotStore>()((set) => ({
  slot: null,
  claim: (slot) => set({ slot }),
  release: (slot) => set((state) => (state.slot === slot ? { slot: null } : state)),
}));

/** A ref for the lane's slot element. Stable, so it attaches once per element. */
export function usePhoneLaneSlotRef() {
  const claim = usePhoneLaneSlot((s) => s.claim);
  const release = usePhoneLaneSlot((s) => s.release);
  return useCallback(
    (node: HTMLElement | null) => {
      if (!node) return;
      claim(node);
      return () => release(node);
    },
    [claim, release],
  );
}
